// lib/supabase.ts
import { createClient, SupabaseClient } from '@supabase/supabase-js'
import type { Database } from './supabase.generated'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// More defensive initialization
let supabase: SupabaseClient<Database> | null = null

export type AuthSessionSource = 'supabase_sdk' | 'fallback_localstorage'

export type AuthSessionResult = {
  data: { session: any | null }
  error: Error | null
  source?: AuthSessionSource
  isFallback?: boolean
}

export type AuthErrorCode = 'SESSION_EXPIRED' | 'SESSION_UNAVAILABLE'
export type ProfilePlanLookupResult = {
  profileExists: boolean
  rawPlan: string | null
  resolvedPlan: 'free' | 'premium'
  source: 'profiles'
}

function createAuthError(code: AuthErrorCode, message: string): Error {
  const error = new Error(message) as Error & { code?: AuthErrorCode }
  error.name = code
  error.code = code
  return error
}

export function getAuthErrorCode(error: unknown): AuthErrorCode | undefined {
  if (!error || typeof error !== 'object') return undefined
  const anyError = error as { code?: unknown; name?: unknown }
  const rawCode = typeof anyError.code === 'string'
    ? anyError.code
    : (typeof anyError.name === 'string' ? anyError.name : undefined)
  if (rawCode === 'SESSION_EXPIRED' || rawCode === 'SESSION_UNAVAILABLE') return rawCode
  return undefined
}

let getSessionInFlight: Promise<AuthSessionResult> | null = null
let getSessionCache: { at: number; result: AuthSessionResult } | null = null
const GET_SESSION_CACHE_MS = 1_500
let authSessionGeneration = 0

type GetSessionOptions = {
  forceFresh?: boolean
}

type SignOutOptions = {
  mode?: 'hard'
}

export function invalidateAuthSessionCache() {
  getSessionCache = null
  authSessionGeneration += 1
  getSessionInFlight = null
}

function timeoutError(label: string, timeoutMs: number): Error {
  return new Error(`${label} timed out after ${timeoutMs}ms`)
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) return error
  return new Error(String(error ?? 'Unknown error'))
}

function resolveProfilePlan(rawPlan: string | null | undefined): 'free' | 'premium' {
  return rawPlan === 'premium' ? 'premium' : 'free'
}

async function withHardTimeout<T>(
  work: () => Promise<T>,
  timeoutMs: number,
  label: string,
  onTimeout?: () => void,
): Promise<T> {
  return await new Promise<T>((resolve, reject) => {
    let settled = false
    const finish = (fn: () => void) => {
      if (settled) return
      settled = true
      fn()
    }
    const timer = setTimeout(() => {
      try { onTimeout?.() } catch {}
      finish(() => reject(timeoutError(label, timeoutMs)))
    }, timeoutMs)

    work().then(
      (value) => finish(() => { clearTimeout(timer); resolve(value) }),
      (error) => finish(() => { clearTimeout(timer); reject(error) }),
    )
  })
}

function extractProjectRef(url?: string): string | null {
  if (!url) return null
  try {
    const host = new URL(url).hostname
    const [projectRef] = host.split('.')
    return projectRef || null
  } catch {
    return null
  }
}

function listAuthStorageKeys(projectRef: string): string[] {
  if (typeof window === 'undefined' || !window.localStorage) return []
  const prefix = `sb-${projectRef}`
  return Object.keys(window.localStorage).filter((key) => key.startsWith(prefix))
}

export function clearLocalAuthArtifacts(projectRef = extractProjectRef(supabaseUrl || undefined)): void {
  invalidateAuthSessionCache()
  if (!projectRef || typeof window === 'undefined' || !window.localStorage) return
  for (const key of listAuthStorageKeys(projectRef)) {
    try {
      window.localStorage.removeItem(key)
    } catch {}
  }
  if (import.meta.env.DEV) {
    console.info('[auth] signed out + local auth cleared', { projectRef })
  }
}

function decodeJwtPayload(accessToken: string): Record<string, any> | null {
  try {
    const parts = accessToken.split('.')
    if (parts.length < 2) return null
    const base64Url = parts[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    const json = atob(padded)
    const payload = JSON.parse(json)
    return payload && typeof payload === 'object' ? payload : null
  } catch {
    return null
  }
}

function readAuthTokenFromLocalStorage(projectRef: string): {
  key: string
  accessToken: string
  refreshToken?: string
  expiresAt?: number
  user?: any
} | null {
  if (typeof window === 'undefined' || !window.localStorage) return null

  const explicitKey = `sb-${projectRef}-auth-token`
  const allKeys = Object.keys(window.localStorage)
  const tokenKey = allKeys.includes(explicitKey)
    ? explicitKey
    : allKeys.find((k) => k.includes(`sb-${projectRef}`) && k.endsWith('-auth-token'))
  if (!tokenKey) return null

  const raw = window.localStorage.getItem(tokenKey)
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as {
      access_token?: string
      refresh_token?: string
      expires_at?: number
      user?: any
    }
    if (!parsed.access_token) return null
    return {
      key: tokenKey,
      accessToken: parsed.access_token,
      refreshToken: parsed.refresh_token,
      expiresAt: parsed.expires_at,
      user: parsed.user,
    }
  } catch {
    return null
  }
}

async function buildFallbackSession(): Promise<AuthSessionResult> {
  const projectRef = extractProjectRef(supabaseUrl || undefined)
  if (!projectRef || !supabaseUrl || !supabaseAnonKey) {
    return {
      data: { session: null },
      error: createAuthError('SESSION_UNAVAILABLE', 'auth.getSession fallback unavailable: missing Supabase config'),
      source: 'fallback_localstorage',
      isFallback: true,
    }
  }

  const token = readAuthTokenFromLocalStorage(projectRef)
  if (!token) {
    return {
      data: { session: null },
      error: createAuthError('SESSION_UNAVAILABLE', 'auth.getSession fallback unavailable: auth token not found in localStorage'),
      source: 'fallback_localstorage',
      isFallback: true,
    }
  }

  const nowSec = Math.floor(Date.now() / 1000)
  const jwtPayload = decodeJwtPayload(token.accessToken)
  const jwtExp = typeof jwtPayload?.exp === 'number' ? jwtPayload.exp : undefined
  const expiresAt = token.expiresAt ?? jwtExp
  const tokenIsFresh = typeof expiresAt === 'number' ? expiresAt > nowSec + 30 : false
  const userFromToken = token.user ?? (
    jwtPayload
      ? {
          id: (jwtPayload.sub as string | undefined) ?? (jwtPayload.id as string | undefined),
          email: jwtPayload.email as string | undefined,
        }
      : null
  )

  // Fast path: if local token is still fresh, avoid /auth/v1/user network call.
  // This prevents connection pileups in Safari/WebKit during repeated sync attempts.
  if (tokenIsFresh && userFromToken?.id) {
    const freshExpiresAt = typeof expiresAt === 'number' ? expiresAt : nowSec + 3600
    const session = {
      access_token: token.accessToken,
      refresh_token: token.refreshToken ?? '',
      token_type: 'bearer',
      expires_at: freshExpiresAt,
      expires_in: Math.max(0, freshExpiresAt - nowSec),
      user: userFromToken,
    } as any
    return {
      data: { session },
      error: null,
      source: 'fallback_localstorage',
      isFallback: true,
    }
  }

  return {
    data: { session: null },
    error: createAuthError('SESSION_EXPIRED', 'SESSION_EXPIRED: sesión local caducada, vuelve a iniciar sesión'),
    source: 'fallback_localstorage',
    isFallback: true,
  }
}

export async function resolveSessionWithFallback(
  getSessionFromSdk: () => Promise<{ data: { session: any | null } }>,
  fallbackBuilder: () => Promise<AuthSessionResult>,
  timeoutMs = 8_000,
): Promise<AuthSessionResult> {
  let sdkError: unknown = null
  try {
    const sdkSession = await withHardTimeout(
      () => getSessionFromSdk(),
      timeoutMs,
      'auth.getSession',
    )
    if (sdkSession.data?.session) {
      const nowSec = Math.floor(Date.now() / 1000)
      const expiresAt = sdkSession.data.session.expires_at
      // With autoRefreshToken: false the SDK never refreshes — it returns the stale session
      // as-is. Treat it as absent if it has expired (30s buffer for clock skew).
      const tokenExpired = expiresAt ? expiresAt < nowSec + 30 : false
      if (!tokenExpired) {
        return {
          data: { session: sdkSession.data.session },
          error: null,
          source: 'supabase_sdk',
          isFallback: false,
        }
      }
      // Token is expired — fall through to fallback.
    }
    // SDK returned null or an expired session — try fallback.
  } catch (e) {
    sdkError = e
  }

  // Wrap fallbackBuilder in the same timeout for deterministic behavior.
  let fallback: AuthSessionResult
  try {
    fallback = await withHardTimeout(() => fallbackBuilder(), timeoutMs, 'auth.fallback')
  } catch (e: unknown) {
    fallback = {
      data: { session: null },
      error: normalizeError(e),
      source: 'fallback_localstorage',
      isFallback: true,
    }
  }
  if (fallback.data.session) {
    return fallback
  }
  // Both SDK and fallback returned no session
  const fallbackCode = getAuthErrorCode(fallback.error)
  const mergedError = fallbackCode
    ? fallback.error
    : sdkError
      ? new Error(
          fallback.error
            ? `${normalizeError(sdkError).message}; fallback failed: ${fallback.error.message}`
            : normalizeError(sdkError).message,
        )
      : (fallback.error ?? createAuthError('SESSION_UNAVAILABLE', 'auth.getSession: no session available'))
  return {
    data: { session: null },
    error: mergedError,
    source: 'fallback_localstorage',
    isFallback: true,
  }
}

// Fetch con timeout y no-cache para mejorar comportamiento en Safari
async function timedFetch(input: RequestInfo | URL, init?: RequestInit & { timeoutMs?: number }): Promise<Response> {
  const url = String(input)

  // Auth endpoints must NOT use AbortController.
  // Aborting /auth/v1/ calls interrupts the SDK's internal auth state machine and,
  // in Safari/WebKit, leaves connections unreleased in the TCP pool — eventually
  // exhausting it and killing all subsequent requests until a hard reload.
  // Auth endpoints are reliable enough to use native fetch with no hard abort.
  if (url.includes('/auth/v1/')) {
    return fetch(input, { ...init, cache: 'no-store' })
  }

  const timeoutMs = init?.timeoutMs ?? 20000
  const controller = new AbortController()
  // Respetar signal externa si existe: abortar si cualquiera lo hace
  let signal: AbortSignal | undefined = controller.signal
  if (init?.signal) {
    const s = init.signal
    if (s.aborted) controller.abort()
    else s.addEventListener('abort', () => controller.abort(), { once: true })
  }
  return await withHardTimeout(
    async () => fetch(input, {
      ...init,
      // no-store evita caches intermedios; omitimos referrerPolicy para compatibilidad con Safari ITP
      cache: 'no-store',
      signal,
    }),
    timeoutMs,
    'timedFetch',
    () => controller.abort(),
  )
}

try {
  if (supabaseUrl && supabaseAnonKey) {
  supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
      auth: {
        // autoRefreshToken disabled: background refresh calls block auth.getSession() and
        // exhaust the Supabase origin TCP connection pool in Safari/WebKit, causing all
        // subsequent requests (including remoteProbe) to hang until hard reload.
        // Reliability-first flow: expired sessions fail fast and require explicit re-login.
        autoRefreshToken: false,
        persistSession: true,
        detectSessionInUrl: true
      },
      global: { fetch: timedFetch as any }
    })
  } else {
    console.warn('Supabase environment variables not found. Auth features will be disabled.')
  }
} catch (error) {
  console.error('Error initializing Supabase:', error)
}

// Export with null check
export { supabase }

// Helper functions for auth that handle null case
export const authHelpers = {
  signUp: async (email: string, password: string, redirectTo?: string) => {
    if (!supabase) return { error: new Error('Supabase not initialized') }
    return await supabase.auth.signUp({
      email,
      password,
      options: redirectTo ? { emailRedirectTo: redirectTo } : undefined,
    })
  },
  
  signIn: async (email: string, password: string) => {
    if (!supabase) return { error: new Error('Supabase not initialized') }
    return await supabase.auth.signInWithPassword({ email, password })
  },

  signInWithGitHub: async () => {
    if (!supabase) return { error: new Error('Supabase not initialized'), data: null }
    const redirectTo = typeof window !== 'undefined' ? `${window.location.origin}/` : undefined
    return await supabase.auth.signInWithOAuth({
      provider: 'github',
      options: {
        redirectTo,
      },
    })
  },

  linkWithGitHub: async () => {
    if (!supabase) return { error: new Error('Supabase not initialized'), data: null }
    const redirectTo = typeof window !== 'undefined' ? `${window.location.origin}/?account_linking=true` : undefined
    return await supabase.auth.linkIdentity({
      provider: 'github',
      options: {
        redirectTo,
      },
    })
  },
  
  signOut: async (options?: SignOutOptions) => {
    if (!supabase) return { error: new Error('Supabase not initialized') }
    const projectRef = extractProjectRef(supabaseUrl || undefined)
    let error: Error | null = null
    try {
      const result = await supabase.auth.signOut()
      error = result.error ?? null
    } catch (e) {
      error = normalizeError(e)
    } finally {
      if ((options?.mode ?? 'hard') === 'hard') {
        clearLocalAuthArtifacts(projectRef)
      }
    }
    return { error }
  },
  
  getSession: async (options?: GetSessionOptions) => {
    if (!supabase) return { data: { session: null }, error: null } as AuthSessionResult
    if (options?.forceFresh) {
      getSessionCache = null
    }
    const now = Date.now()
    if (!options?.forceFresh && getSessionCache && now - getSessionCache.at < GET_SESSION_CACHE_MS) {
      return getSessionCache.result
    }
    if (getSessionInFlight) return getSessionInFlight

    const generationAtStart = authSessionGeneration
    getSessionInFlight = resolveSessionWithFallback(
      () => supabase!.auth.getSession(),
      buildFallbackSession,
      8_000,
    ).then((result) => {
      if (generationAtStart !== authSessionGeneration) {
        return {
          data: { session: null },
          error: createAuthError('SESSION_UNAVAILABLE', 'auth.getSession invalidated after logout'),
          source: 'fallback_localstorage' as const,
          isFallback: true,
        }
      }
      if (result.isFallback && result.error === null) {
        console.warn('[auth] getSession fallback_localstorage active')
      }
      getSessionCache = { at: Date.now(), result }
      return result
    }).finally(() => {
      getSessionInFlight = null
    })

    return getSessionInFlight
  },
  
  onAuthStateChange: (callback: (event: string, session: any) => void) => {
    if (!supabase) {
      return { data: { subscription: { unsubscribe: () => {} } } }
    }
    return supabase.auth.onAuthStateChange(callback)
  }
}

export async function fetchProfilePlan(opts: {
  userId: string
  accessToken?: string | null
}): Promise<ProfilePlanLookupResult> {
  if (!opts.userId) {
    return {
      profileExists: false,
      rawPlan: null,
      resolvedPlan: 'free',
      source: 'profiles',
    }
  }

  try {
    if (opts.accessToken && supabaseUrl && supabaseAnonKey) {
      const url = new URL(`${supabaseUrl}/rest/v1/profiles`)
      url.searchParams.set('select', 'plan')
      url.searchParams.set('user_id', `eq.${opts.userId}`)
      url.searchParams.set('limit', '1')

      const response = await timedFetch(url, {
        headers: {
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${opts.accessToken}`,
          Accept: 'application/json',
        },
        timeoutMs: 12_000,
      })

      if (!response.ok) {
        throw new Error(`profiles lookup failed with status ${response.status}`)
      }

      const payload = await response.json() as Array<{ plan?: string | null }> | null
      const row = Array.isArray(payload) ? (payload[0] ?? null) : null
      return {
        profileExists: Boolean(row),
        rawPlan: row?.plan ?? null,
        resolvedPlan: resolveProfilePlan(row?.plan ?? null),
        source: 'profiles',
      }
    }

    if (supabase) {
      const { data, error } = await supabase
        .from('profiles')
        .select('plan')
        .eq('user_id', opts.userId)
        .maybeSingle()

      if (error) {
        throw error
      }

      return {
        profileExists: Boolean(data),
        rawPlan: data?.plan ?? null,
        resolvedPlan: resolveProfilePlan(data?.plan ?? null),
        source: 'profiles',
      }
    }
  } catch (error) {
    throw normalizeError(error)
  }

  return {
    profileExists: false,
    rawPlan: null,
    resolvedPlan: 'free',
    source: 'profiles',
  }
}

// Diagnóstico rápido de conectividad a Supabase (útil en Safari)
// Pass `accessToken` to skip the internal getSession() call — avoids an
// extra /auth/v1/user round-trip when the caller has already resolved auth.
export async function diagnoseSupabase(opts?: { accessToken?: string }): Promise<{ reached: boolean; status?: number; ok?: boolean; error?: string; authSource?: AuthSessionSource }> {
  if (!supbaseUrlPresent()) return { reached: false, error: 'Supabase env vars missing' }
  let authSource: AuthSessionSource | undefined
  let token: string | undefined = opts?.accessToken
  try {
    if (!token) {
      const sessionResult = await authHelpers.getSession()
      authSource = sessionResult.source
      token = sessionResult.data?.session?.access_token
    }
    const url = `${supabaseUrl}/rest/v1/projects?select=id&limit=1`
    const res = await withHardTimeout(
      () => timedFetch(url, {
        headers: {
          'apikey': supabaseAnonKey!,
          'Authorization': token ? `Bearer ${token}` : `Bearer ${supabaseAnonKey}`,
          'Accept': 'application/json',
        },
        timeoutMs: 12_000,
      }),
      12_000,
      'diag.rest.projects',
    )
    return { reached: true, status: res.status, ok: res.ok, authSource }
  } catch (e: any) {
    return { reached: false, error: e?.message || String(e), authSource }
  }
}

function supbaseUrlPresent() { return Boolean(supabaseUrl && supabaseAnonKey) }

// En dev, expón un helper para correr desde la consola del navegador
if (import.meta.env.DEV && typeof window !== 'undefined') {
  ;(window as any).wrlensDiag = diagnoseSupabase
}
